import { Stack } from '@mui/material';

import Note, { type NoteColour } from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import { CARER_INITIALS } from '@/karehero-app/tell-us-once/intake/content';
import BookCallScreen from '@/karehero-app/tell-us-once/intake/BookCallScreen';
import CarriedScreen from '@/karehero-app/tell-us-once/benefits/CarriedScreen';
import ResultScreen from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import CalculatorFormScreen from '@/karehero-app/tell-us-once/benefits/CalculatorFormScreen';
import { FORM_FIELDS } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';
import DocumentsReadScreen from '@/karehero-app/tell-us-once/benefits/DocumentsReadScreen';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import {
  CALCULATOR_CHAT,
  CALCULATOR_KEEP_ANSWERS,
  EVERY_QUESTION,
} from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';

const noop = () => {};

/**
 * What changed after Alex's review of the question set (#prod-execution,
 * James replying), and why. Keyed by question id so the warm path and the cold
 * path carry the same note under the same question.
 */
const CHANGED_TITLE = 'Changed, 5 October';

const changed = (body: string, colour: NoteColour = 'sunflower') => (
  <Note title={CHANGED_TITLE} colour={colour} variant='body2'>
    {body}
  </Note>
);

const CHANGED: Record<string, string> = {
  'care-recipient-date-of-birth':
    "Four bands, not three. Under 18 rules people out of Universal Credit, and 60 is the bus pass age outside England. Alex's point 1, and James agreed. The state pension age is moving to 67 and a band can't place anyone against it. We're accepting that rather than asking for a date of birth.",
  'care-recipient-condition-duration':
    "One answer each. The old options were 'Six months or more', 'Expected to last a year' and 'Neither', so somebody with both had nothing to pick. Six months already settles it, so the future only comes in when it's under six months. 'Not sure' leaves it unanswered. Alex's point 6.",
  'care-recipient-disability-type':
    "New. Only asked if they have a disability and are under the bus pass age: 66 in England, 60 in Wales. Over it, age alone qualifies. The bus pass rules each need a named disability, so a yes on its own isn't enough. Alex's point 2. James wants the bus pass kept.",
  'care-recipient-terminally-ill':
    "Asked of everyone now, not only after a yes to the disability question. It's a route into Attendance Allowance at any age, and the warm path never asked it, so we'd lost it. Alex's point 5.",
  'proto-existing-awards':
    "By benefit, not by part. Attendance Allowance, PIP, DLA, None of these, Not sure. Which part of PIP or DLA is a follow-up, asked only of somebody who has it. Adult Disability Payment and Pension Age Disability Payment are new, and only offered in Scotland. 'Not sure' leaves it unanswered rather than counting as none. Alex's point 3, with James's list. It was one list of nine, which ran off the screen.",
  'proto-pip-parts':
    "New. Only asked if they get PIP. Daily living qualifies a carer for Carer's Allowance; mobility is a route to a Blue Badge.",
  'proto-dla-parts':
    "New. Only asked if they get DLA. Care at the middle or highest rate qualifies a carer for Carer's Allowance; higher rate mobility is a route to a Blue Badge. The lower care rate does neither, hence 'Neither of those'.",
  'proto-adp-parts':
    "New, Scotland only. Daily living qualifies a carer for Carer Support Payment. Mobility is asked so it reads like PIP, but nothing uses it yet: the Scottish Blue Badge route isn't in the engine.",
};

// The latest round, the awards split, in green so it can be told apart from
// the first round until it has been reviewed.
const LATEST = new Set([
  'proto-existing-awards',
  'proto-pip-parts',
  'proto-dla-parts',
  'proto-adp-parts',
]);

/**
 * Round three: what came out of the call with Alex. Coral, so it can be told
 * apart from the two rounds before it.
 */
const round3 = (body: string, title = 'Round 3, 5 October') => (
  <Note
    title={title}
    tone={{ head: 'custom.coral.100', frame: 'custom.coral.400' }}
    variant='body2'
  >
    {body}
  </Note>
);

const ROUND3: Record<string, string> = {
  'care-recipient-is-disabled':
    'Moved to the end, with the other condition questions. They are the ones that can be talked through, and grouped last they stay out of the run of quick taps.',
  'care-recipient-condition-duration':
    'Two questions now, not one with four answers. This one is yes, no or not sure; a no asks whether it will last a year. One thing per question is easier to fill in from what somebody said, which is what Alex needs. Most people this age answer yes and never see the second.',
  'care-recipient-condition-expected':
    'New. Only asked after a no to six months. Not sure leaves the duration unanswered.',
  'care-recipient-terminally-ill':
    'Moved to the end, with the other condition questions.',
  'care-recipient-disability-type':
    "Talked or typed, not seven rows. As rows it ran off the screen, which is what Alex flagged. In the warm path it's part of the opening turn about her health instead, read with the paperwork. This row is every question with nothing known, so it's still asked here; the quiz and the form keep the list.",
};

const changedFor = (id: string) => {
  const first =
    CHANGED[id] && changed(CHANGED[id], LATEST.has(id) ? 'green' : 'sunflower');
  const third = ROUND3[id] && round3(ROUND3[id]);
  if (first && third) {
    return (
      <Stack spacing={2}>
        {first}
        {third}
      </Stack>
    );
  }
  return first || third || undefined;
};

// The calculator's bar, on every screen inside it. Built here rather than by
// each screen so the row cannot end up with two versions of the same bar.
const bar = <CalculatorBar />;

/**
 * Where a question sits in the conversation, by id.
 *
 * Named rather than counted. The steps below used to index in by number, and
 * adding the paperwork and reading turns at the front moved every one of them
 * at once, silently, because a number is always a valid index.
 */
const at = (id: string) => {
  const i = CALCULATOR_CHAT.findIndex((q) => q.id === id);
  if (i < 0) throw new Error(`No calculator question called ${id}`);
  return i;
};

// The chat, opened at one of its questions. Same component the intake uses, so
// the dots, the teal rows and the uploads come with it rather than being drawn
// again.
const chat = (
  step: number,
  extra: Partial<Parameters<typeof ChatV2>[0]> = {},
) => (
  <ChatV2
    questions={CALCULATOR_CHAT}
    startAt={step}
    keepAnswers={CALCULATOR_KEEP_ANSWERS}
    onExit={noop}
    bar={bar}
    {...extra}
  />
);

// The cold path: the same chat, asking the whole set. `fresh` so each question
// is the screen rather than the bottom of a twenty-three-answer transcript.
const cold = (step: number) => (
  <ChatV2
    questions={EVERY_QUESTION}
    startAt={step}
    fresh
    onExit={noop}
    bar={bar}
  />
);

/**
 * PROPOSED. The benefits calculator, laid out in the patterns Tell Us Once
 * established.
 *
 * Today the calculator is a hole in the intake, drawn as one: two placeholder
 * screens between the questions and the claim. The question set exists, and it
 * is a twenty-three-question form behind a Continue.
 *
 * The proposal is that the calculator is the same conversation the intake is. It
 * opens on what we already hold, confirms rather than re-asks, offers the award
 * letter exactly where the three questions nobody can answer off the top of
 * their head begin, reads the letter while the questions carry on, and ends on a
 * claim rather than a number.
 */
export const benefitsCalculatorFlowsSpec: HandoverSpec = {
  id: 'benefits-calculator-concept',
  title: 'Benefits calculator',
  description: '',
  links: [
    {
      label: 'Exploration: every way of answering it',
      url: '/tell-us-once/benefits-calculator',
    },
    { label: 'The question set, walkable', url: '/tell-us-once/ai-chat' },
  ],
  flows: [
    {
      title: 'If we already hold some of it',
      steps: [
        {
          id: 'choose',
          title: 'Where it starts',
          tap: '‘Benefits calculator’',
          // The profile's Assess tab, which is where the calculator lives and
          // where a finished one puts you back. It used to be the fork the
          // intake hands straight to, and that is the one moment nobody
          // repeats: every later visit starts from a tab.
          //
          // With the account bar, because the profile is somewhere you are in
          // the app rather than a step inside a flow.
          render: () => (
            <CareProfileScreen
              initialTab='claims'
              account={{ initials: CARER_INITIALS }}
            />
          ),
          // The other way in. Same calculator either way.
          alt: {
            title: 'From Home',
            render: () => <HomeScreen />,
          },
        },
        {
          id: 'carried',
          title: 'Three we already have',
          tap: 'Tap ‘Start’',
          render: () => <CarriedScreen onStart={noop} bar={bar} />,
        },
        {
          id: 'documents-first',
          title: 'Her health, and any paperwork',
          tap: 'Into the calculator',
          render: () => chat(at('paperwork'), { fresh: true }),
          note: (
            <Stack spacing={2}>
              <Note title='Readme' variant='body2'>
                Offered in the conversation rather than on a screen of its own.
                A full page with a primary button reads as a gate to clear
                before you are allowed to start, which is a poor first move in a
                thing people open because they are worried about money. The
                reading is what makes the offer worth taking, so it happens here
                rather than at the end.
              </Note>
              {round3(
                "Her health first, with the paperwork, as one turn they can talk, type or add files to. Both are read after they finish, so one reading covers what they said and what they sent, and the questions after it are only what's left. It replaces the talked disability question and the 'anything else' that were at the end.",
              )}
            </Stack>
          ),
        },
        {
          id: 'documents-added',
          title: 'A couple of things handed over',
          tap: '‘Add paperwork’',
          render: () =>
            chat(at('paperwork'), {
              fresh: true,
              uploaded: 'document',
              uploadedName: ['Award letter.jpg', 'Pension statement.pdf'],
            }),
        },
        {
          id: 'documents-reading',
          title: 'Reading what they sent',
          tap: '‘Continue’',
          render: () => chat(at('processing'), { reading: true }),
          alt: {
            // The other way out of the paperwork turn. Continue sits under the
            // offer whether or not anything was handed over.
            title: 'Or straight in, with nothing to read',
            render: () => chat(at('confirm-location'), { fresh: true }),
          },
          note: (
            <Note title='Readme' variant='body2'>
              The same conversation, not a screen. Continue adds the reading
              under the paperwork turn, the same as any other reply, and the
              dots stay under it until the reading is done. Nothing is cleared,
              so every answer stays there to come back to. Continue with nothing
              handed over goes straight to the questions, which is the row
              underneath: there is nothing to read, so there is nothing to say
              about reading it.
            </Note>
          ),
        },
        {
          id: 'documents-read',
          title: 'What it answered, before the questions',
          tap: 'It finishes reading',
          render: () => <DocumentsReadScreen bar={bar} />,
          note: (
            <Note title='Readme' variant='body2'>
              The same read-back that used to sit at the end of the flow. There
              it was a summary; here it is the payoff, because every row on it
              is a question they are no longer being asked.
            </Note>
          ),
        },
        {
          id: 'confirm',
          title: 'Confirm, do not re-ask',
          tap: '‘Yes, still England’',
          render: () => chat(at('confirm-location')),
        },
        {
          id: 'age',
          title: 'Her age, in four bands',
          tap: '‘66 or over’',
          render: () => chat(at('age')),
          note: changedFor('care-recipient-date-of-birth'),
        },
        {
          id: 'turns-on',
          title: 'The one the claim turns on',
          tap: '‘Day and night’',
          render: () => chat(at('needs-help')),
        },
        {
          id: 'letter',
          title: 'The three nobody knows',
          tap: '‘Photograph the award letter’',
          render: () => chat(at('already-gets')),
          note: (
            <Stack spacing={2}>
              <Note title='Readme' variant='body2'>
                Savings, income and what is already in payment are the three a
                conversation should not be trusted with, and all three are
                printed on a letter somebody already has. The offer sits where
                those questions begin rather than at the start, where an upload
                has no reason yet.
              </Note>
              {changed(
                "Pick several, by benefit rather than three names in the question. Which part of PIP or DLA is a follow-up, only for somebody who has it, so Margaret never sees one. 'Not sure' is new. She lives in England, so the two Scottish awards are left off. Alex's point 3, with James's list.",
                'green',
              )}
            </Stack>
          ),
        },
        {
          id: 'reading',
          title: 'Read while the questions carry on',
          tap: 'The next question, straight away',
          render: () =>
            chat(at('savings'), {
              uploaded: 'document',
              uploadedName: 'Award letter.jpg',
            }),
        },
        {
          id: 'how-long',
          title: 'How long, one thing at a time',
          tap: '‘Yes’',
          render: () => chat(at('how-long')),
          note: round3(
            "Yes, no or not sure, and moved to the end with the other condition questions. A no would ask whether it will last a year; Margaret has needed help for years, so she doesn't see it. Asked as needing help, the way Attendance Allowance asks it. When the reading at the start already answers it, or a terminal illness makes it irrelevant, it isn't asked; the prototype still asks it because its reading is pretend.",
          ),
        },
        {
          id: 'terminal',
          title: 'Terminal illness, asked of everyone',
          tap: '‘No’',
          render: () => chat(at('terminal')),
          note: changedFor('care-recipient-terminally-ill'),
        },
        {
          id: 'result',
          title: 'What she could claim',
          tap: 'The last answer',
          render: () => <ResultScreen onStart={noop} onTalk={noop} bar={bar} />,
          note: (
            <Stack spacing={2}>
              <Note title='Readme' variant='body2'>
                The amount is on the row rather than in a headline. One big
                number is a promise, and this is an estimate a claim decides.
              </Note>
              {changed(
                "A range, not a rate. We can't tell the higher rate from the lower, so it shows £76.70 to £114.60 a week, as the live calculator does. The £110.40 it used to show was last year's higher rate. Alex's point 4, James's call.",
              )}
              {round3(
                "No amounts except Attendance Allowance's range: the calculator says whether, not how much, so Pension Credit is 'Likely, once Attendance Allowance is in' rather than '£82 a week'. Carer's Allowance is on it as the carer's own claim, blocked until Attendance Allowance is awarded. The title no longer counts, because the claims are not all hers.",
              )}
            </Stack>
          ),
        },
        {
          id: 'call',
          title: 'Booking the call',
          tap: '‘Book a call’',
          render: () => <BookCallScreen />,
        },
        {
          // The other way out, and the reason the bar carries an X at all: a
          // flow you can only leave by closing the app is one people close.
          id: 'leave',
          title: 'Or leave, and come back',
          tap: 'The X, at any point',
          render: () =>
            chat(at('needs-help'), {
              bar: <CalculatorBar initialConfirming />,
            }),
        },
      ],
      noteAfter: (
        <Stack spacing={2}>
          <Note title='Waiting on' colour='cherry' variant='body2'>
            <Stack spacing={1.5}>
              <span>
                From Alex's review of the question set. These are still open, so
                the screens don't answer them yet.
              </span>
              <span>
                Bus pass list. The disability question uses the six types the
                engine already reads. Not yet confirmed as the full list, and
                one route looks missing: somebody who would be refused a driving
                licence on medical grounds, such as for seizures. To check
                against the gov.uk criteria.
              </span>
              <span>
                Bus pass in Scotland and NI. James's reply covers all four
                nations, but the engine has no disability route there yet, so
                the question isn't asked there.
              </span>
              <span>
                Carer's Allowance and Blue Badge in V1. Wanted, unless it's a
                lot of extra work. The result now shows Carer's Allowance as
                blocked; if it's out of V1, that row goes. Blue Badge isn't on
                it yet.
              </span>
              <span>
                Carer Support Payment. In Scotland the carer benefit should read
                Carer Support Payment, not Carer's Allowance. Not drawn, because
                this path is England.
              </span>
              <span>
                Terminal illness and disability on the warm path. Whether the
                calculator asks them itself, as drawn, or takes them from the
                intake when it already has them.
              </span>
            </Stack>
          </Note>
          <Note
            title='Round 3: to decide'
            tone={{ head: 'custom.coral.100', frame: 'custom.coral.400' }}
            variant='body2'
          >
            <Stack spacing={1.5}>
              <span>
                From the call with Alex. Not drawn, because each is a call.
              </span>
              <span>
                How Carer's Allowance, Carer Support Payment and Blue Badge are
                worked out from the questions. Alex asked for this. The
                prototype engine already does it, in
                benefits-flow/engine/rules.ts and action.ts. Carer's Allowance:
                someone caring unpaid, 16 or over, not in full-time education,
                35 hours a week or more, earning under £204 a week. Claimable
                only once the person they care for gets Attendance Allowance,
                PIP daily living, DLA care at the middle or highest rate, Adult
                Disability Payment daily living or Pension Age Disability
                Payment. In Scotland the same answers mean Carer Support
                Payment. Blue Badge: a disability, then automatic with DLA
                higher rate mobility, a check of the award letter with PIP
                mobility, and the council's discretion otherwise. Who writes it
                up for Alex is still open.
              </span>
              <span>
                A question we don't need. Alex is working out when an answer
                makes another question pointless, such as a terminal illness
                making how long irrelevant. The proposal: skip it without saying
                so, the same as a conditional question that doesn't apply.
                Confirming it would be asking a question we don't need.
              </span>
              <span>
                Talk or tap. The calculator talks once, at the start, about her
                health, and is rows after that. The proposal is to keep it that
                way rather than add the orb everywhere and take it away later.
              </span>
              <span>
                Changing an answer. Tapping a reply opens a sheet. If that turns
                out expensive to build, the agreed fallback is a step back, or
                Start again when leaving the calculator.
              </span>
            </Stack>
          </Note>
        </Stack>
      ),
    },
    {
      title: 'Nothing but a name: every question',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The whole set, in the order the engine asks it. Twenty questions,
              every one of them a tap, and this is what somebody who told us
              nothing useful has to get through.
            </span>
            <span>
              Worth reading as the cost of not knowing anything: the section
              under this one is the same calculator when the conversation has
              already answered some of it, and it is eleven questions and a
              photograph.
            </span>
            <span>
              The prompts and options are the real screener's, so this row is
              the question set rather than a drawing of it. Conditionals prune
              it per person, so nobody sees all twenty-three: the bus pass,
              award, nursing and partner questions only open if the answers
              before them call for them.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        ...EVERY_QUESTION.map((q, i) => ({
          id: `all-${q.id}`,
          title: q.ask,
          tap: i === 0 ? 'Open the calculator' : q.answer,
          render: () => cold(i),
          note: changedFor(q.id),
        })),
        {
          // The row would otherwise stop mid-air on the last question. It ends
          // where the other section ends, because it is the same calculator.
          id: 'all-result',
          title: 'The same result, twenty-three answers later',
          tap: 'The last answer',
          render: () => <ResultScreen onStart={noop} onTalk={noop} bar={bar} />,
        },
      ],
    },
    {
      // Collapsed: it is here to be looked at when somebody wants to compare,
      // not to be scrolled past on the way to the work.
      title: 'If it were a form instead',
      collapsed: true,
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The same twenty-three questions, from the same screener, delivered
              as one form. The prompts, the options and the order are the real
              ones, so what this shows is the question set rather than a
              strawman built to lose.
            </span>
            <span>
              A form has to show every question before it knows a single answer.
              The conversation prunes as it goes and nobody sees all
              twenty-three; here the bus pass, award, nursing and partner
              questions are on screen for everyone whether they apply or not.
            </span>
          </Stack>
        </Note>
      ),
      // One step per question, as the chat row does, so the two rows are the
      // same twenty-three questions side by side rather than a full set against a
      // handful of samples.
      steps: [
        ...FORM_FIELDS.map((f, i) => ({
          id: `form-${f.id}`,
          title: f.label,
          tap: i === 0 ? 'Open the calculator' : 'Next',
          render: () => <CalculatorFormScreen question={f.id} />,
          note: changedFor(f.id),
        })),
        {
          // The same ending as the other two rows. A comparison that stops at
          // the last question compares the asking and not the answer, and the
          // answer is the thing both shapes exist to produce: it is identical,
          // which is the point.
          id: 'form-result',
          title: 'The same result, one long form later',
          tap: '‘See results’',
          render: () => <ResultScreen onStart={noop} onTalk={noop} bar={bar} />,
        },
      ],
    },
    {
      title: 'Changing an answer',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Scrolled back up, on a question already answered. The rows go once
              a question is answered, the same as the intake, and the answer
              stays as their reply, with the earlier ones above it.
            </span>
            <span>
              Tapping the reply opens the question's rows in a sheet, with the
              answer on the record ticked. Picking another changes it straight
              away: no confirm, the sheet closes and the reply updates.
            </span>
            <span>
              It says the answer changed, and nothing else. What that did to the
              claims belongs on the result screen, which is worked out again
              anyway. A reply that starts listing what dropped out is answering
              a question nobody has asked yet.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'change-back',
          title: 'Back up the transcript',
          tap: 'Tap ‘66 or over’',
          render: () => chat(at('how-long'), { changeAt: { at: at('age') } }),
        },
        {
          id: 'change-sheet',
          title: 'The question, in a sheet',
          tap: '‘60 to 65’',
          render: () =>
            chat(at('how-long'), {
              changeAt: { at: at('age'), open: true },
            }),
        },
        {
          id: 'change-noted',
          title: 'Changed, and nothing else',
          tap: 'Back to the result',
          render: () =>
            chat(at('how-long'), {
              changeAt: { at: at('age'), to: '60 to 65' },
            }),
        },
        {
          id: 'change-result',
          title: 'The result, worked out again',
          tap: 'Where the change shows',
          render: () => <ResultScreen onStart={noop} onTalk={noop} bar={bar} />,
        },
      ],
    },
  ],
  states: [],
  components: {},
};
